import { type ReactNode, useEffect, useRef, useState } from "react";

export function MessageComposer({
	id,
	label,
	busy,
	onSend,
	onTyping,
	onCancel,
	clearOnSubmit = false,
	draftRequest,
	children,
}: {
	id: string;
	label: string;
	busy: boolean;
	onSend: (message: string) => Promise<boolean>;
	onTyping?: () => void;
	onCancel?: () => Promise<void>;
	clearOnSubmit?: boolean;
	draftRequest?: { key: string; message: string };
	children?: ReactNode;
}) {
	const input = useRef<HTMLTextAreaElement>(null);
	const [hasDraft, setHasDraft] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [cancelling, setCancelling] = useState(false);
	const locked = busy || submitting || cancelling;
	useEffect(() => {
		if (!draftRequest || !input.current) return;
		input.current.value = draftRequest.message;
		setHasDraft(Boolean(draftRequest.message.trim()));
		input.current.focus({ preventScroll: true });
	}, [draftRequest]);

	async function submit() {
		const message = input.current?.value ?? "";
		if (locked || !message.trim()) return;
		setSubmitting(true);
		if (clearOnSubmit && input.current) {
			input.current.value = "";
			setHasDraft(false);
		}
		try {
			if ((await onSend(message)) && !clearOnSubmit) {
				if (input.current) input.current.value = "";
				setHasDraft(false);
			}
		} finally {
			setSubmitting(false);
		}
	}

	async function cancel() {
		if (!onCancel || locked) return;
		setCancelling(true);
		try {
			await onCancel();
		} finally {
			setCancelling(false);
		}
	}

	return (
		<>
			<label htmlFor={id}>{label}</label>
			<textarea
				id={id}
				ref={input}
				defaultValue=""
				onInput={(event) => {
					const nextHasDraft = event.currentTarget.value.trim().length > 0;
					setHasDraft(nextHasDraft);
					if (nextHasDraft) onTyping?.();
				}}
			/>
			<div className="action-row message-composer-actions">
				<button
					type="button"
					className="message-send-button"
					onClick={submit}
					disabled={!hasDraft || locked}
					aria-busy={submitting || busy}
				>
					Send message
				</button>
				{onCancel && hasDraft && (
					<button type="button" className="secondary" onClick={cancel} disabled={locked} aria-busy={cancelling}>
						Cancel contribution
					</button>
				)}
				{children}
			</div>
		</>
	);
}
